import type { FieldDefinition } from "@/types/template";

/**
 * Starter component every new workspace session opens with, so the user can
 * begin hand-editing (or wiring fields with the fields tool) immediately —
 * prompting is optional. Keep it boring and legible: it's a scaffold, not a
 * showcase. The props/fields pairing follows the same conventions the
 * generator emits (text-rich for every user-editable string — the backend
 * rejects new templates with plain text fields), so the Fields tab, preview,
 * renderer, AND save-as-template all work on it unchanged.
 */
export const STARTER_TSX = `import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";

type RichText = {
  text: string;
  font: string;
  fontSize: number;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
};

type Props = {
  title: RichText;
  accentColor: string;
};

const fontStack = (font: string) => \`'\${font}', system-ui, sans-serif\`;

export default function UserComponent({ title, accentColor }: Props) {
  const frame = useCurrentFrame();

  const opacity = interpolate(frame, [0, 20], [0, 1], {
    extrapolateRight: "clamp",
  });
  const rise = interpolate(frame, [0, 20], [24, 0], {
    extrapolateRight: "clamp",
  });
  const underline = interpolate(frame, [10, 35], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "transparent",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div style={{ opacity, transform: \`translateY(\${rise}px)\`, textAlign: "center" }}>
        <div
          style={{
            fontFamily: fontStack(title.font),
            fontSize: title.fontSize,
            fontWeight: title.bold ? 700 : 400,
            fontStyle: title.italic ? "italic" : "normal",
            textDecoration: title.underline ? "underline" : "none",
            color: "#ffffff",
          }}
        >
          {title.text}
        </div>
        <div
          style={{
            height: 6,
            marginTop: 16,
            borderRadius: 3,
            backgroundColor: accentColor,
            transform: \`scaleX(\${underline})\`,
          }}
        />
      </div>
    </AbsoluteFill>
  );
}
`;

export const STARTER_FIELDS: FieldDefinition[] = [
  {
    key: "title",
    label: "Title",
    type: "text-rich",
    default: { text: "Your title here", font: "Rubik", fontSize: 96, bold: true },
    required: true,
  },
  {
    key: "accentColor",
    label: "Accent color",
    type: "color",
    default: "#4f8ef7",
    required: false,
  },
];
